import React from 'react';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { displayPrice } from '@common/utils';

import {
  GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_matching_catalog_product_info_shipping_fee as MatchingProductShippingFee,
  PdpShippingFeeType,
} from '../../graphql/select-option_types.2';
import { GetCatalogProductDetailPageOption_pdp_option_info_catalog_product_shipping_fee as ProductShippingFee } from '../../graphql/select-option_types.2';

interface ShippingFeeProps {
  shippingFee: ProductShippingFee | null;
  matchingShippingFee?: MatchingProductShippingFee | null;
  totalPrice: number;
  generalTotalPrice: number;
  isZigzinProduct: boolean;
  isMatchingProduct: boolean;
  isZigzinProductFreeShipping: boolean;
  isMatchingAll: boolean;
  hasMatchingZigzin: boolean;
  hasMatchingGeneral: boolean;
}

export const ShippingFee: React.FC<React.PropsWithChildren<ShippingFeeProps>> = ({
  shippingFee,
  matchingShippingFee,
  totalPrice,
  generalTotalPrice,
  isZigzinProduct,
  isMatchingProduct,
  isZigzinProductFreeShipping,
  isMatchingAll,
  hasMatchingZigzin,
  hasMatchingGeneral,
}) => {
  if (!shippingFee) {
    return null;
  }
  if (shippingFee.base_fee === null) {
    return <>정보없음</>;
  }

  const free_shipping_amount = Number(shippingFee.minimum_free_shipping_fee ?? 0);
  const free_shipping_matching_amount = Number(matchingShippingFee?.minimum_free_shipping_fee ?? 0);

  const is_free_shipping_shop = shippingFee.base_fee === 0;
  const is_free_shipping_product = shippingFee.fee_type === PdpShippingFeeType.FREE;
  const is_more_than_free_shipipng_amount =
    (free_shipping_amount > 0 && totalPrice >= free_shipping_amount) ||
    (free_shipping_matching_amount > 0 && totalPrice >= free_shipping_matching_amount);

  const isMoreThanFreeGeneralShippingAmount =
    (free_shipping_amount > 0 && generalTotalPrice >= free_shipping_amount) ||
    (free_shipping_matching_amount > 0 && generalTotalPrice >= free_shipping_matching_amount);

  const FreeShippingFeeTag = <>무료배송</>;
  const NotFreeShippingFeeTag = <>무료배송~{displayPrice(990)}</>;

  const MatchingZigzinShippingFeeTag = isZigzinProductFreeShipping ? FreeShippingFeeTag : NotFreeShippingFeeTag;

  if (isMatchingAll) {
    const generalShippingFee = (isZigzinProduct ? matchingShippingFee?.base_fee : shippingFee.base_fee) ?? 0;
    const isGeneralShippingFeeFree = generalShippingFee === 0 || isMoreThanFreeGeneralShippingAmount;

    return isZigzinProductFreeShipping && isGeneralShippingFeeFree ? (
      FreeShippingFeeTag
    ) : (
      <ShippingAmount>
        <Name>직진</Name>
        <Dot />
        <Value>{MatchingZigzinShippingFeeTag}</Value>
        <Divider />
        <Name>일반</Name>
        <Dot />
        <Value>{isGeneralShippingFeeFree ? FreeShippingFeeTag : displayPrice(generalShippingFee)}</Value>
      </ShippingAmount>
    );
  }

  if (isZigzinProduct) {
    if (isMatchingProduct && hasMatchingZigzin) {
      return isZigzinProductFreeShipping ? FreeShippingFeeTag : NotFreeShippingFeeTag;
    }

    if (hasMatchingGeneral) {
      const is_free_shipping_matching_shop = matchingShippingFee?.base_fee === 0;
      const is_free_shipping_matching_product = matchingShippingFee?.fee_type === PdpShippingFeeType.FREE;
      const is_free_shipping_amount_matching_product =
        is_free_shipping_matching_shop || is_free_shipping_matching_product || is_more_than_free_shipipng_amount;

      const matchingGeneralFeeShippingTag = (
        <>
          {displayPrice(matchingShippingFee?.base_fee ?? 0)}
          {free_shipping_matching_amount > 0 && (
            <FreeShippingAmount>
              <Dot />
              {displayPrice(free_shipping_matching_amount)} 이상 무료
            </FreeShippingAmount>
          )}
        </>
      );
      return is_free_shipping_amount_matching_product ? FreeShippingFeeTag : matchingGeneralFeeShippingTag;
    }
  }

  if (!isZigzinProduct && hasMatchingZigzin) {
    return MatchingZigzinShippingFeeTag;
  }

  if (is_free_shipping_shop || is_free_shipping_product || is_more_than_free_shipipng_amount) {
    if (isZigzinProduct) {
      return isZigzinProductFreeShipping ? FreeShippingFeeTag : NotFreeShippingFeeTag;
    }
    return FreeShippingFeeTag;
  }

  return (
    <>
      <ShippingText>배송비</ShippingText>
      {displayPrice(shippingFee.base_fee)}
      {free_shipping_amount > 0 && (
        <FreeShippingAmount>
          <Dot />
          {displayPrice(free_shipping_amount)} 이상 무료
        </FreeShippingAmount>
      )}
    </>
  );
};

const ShippingText = styled.div`
  margin: 0 4px 0 0;
`;

const Dot = styled.span`
  margin: 0 3px;
  position: relative;
  top: -4px;
  display: inline-block;
  width: 2px;
  height: 2px;
  background-color: ${vars.v3Color.gray200};
`;

const Divider = styled.span`
  position: relative;
  top: 1px;
  margin: 0 5px;
  display: inline-block;
  width: 1px;
  height: 12px;
  background-color: ${vars.v3Color.gray200};
`;

const FreeShippingAmount = styled.span`
  font-weight: 400;
  color: ${vars.v3Color.gray500};
`;

const ShippingAmount = styled.span`
  position: relative;
  color: ${vars.v3Color.gray700};
`;

const Name = styled.span`
  font-weight: 500;
`;

const Value = styled.span`
  font-weight: 400;
`;
